/**
 * A searchable checklist popover: type to narrow, tick any subset.
 *
 * `options` is [{ value, label, color?, hint? }] and `selected` an array of
 * values; an empty selection reads as `allLabel` ("All sources"), which is how
 * the reports using it treat it — no filter. Select all / Clear act on the
 * options the search is showing, so "search 'spring', Select all" picks every
 * spring campaign and nothing else.
 *
 * ClinicMultiSelect is the same idea without search, for a short fixed list.
 */

import { useMemo, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { Button, Checkbox, Input, Popover, PopoverTrigger, PopoverContent } from './ui'

export function triggerText(options, selected, allLabel, noun) {
  if (!selected.length) return allLabel
  if (selected.length === 1) return options.find(o => o.value === selected[0])?.label ?? selected[0]
  return `${selected.length} ${noun}`
}

export function filterOptions(options, query) {
  const q = query.trim().toLowerCase()
  return q ? options.filter(o => String(o.label).toLowerCase().includes(q)) : options
}

export default function SearchMultiSelect({
  options, selected, onChange, allLabel = 'All', noun = 'selected',
  searchPlaceholder = 'Search…', className = 'w-48', disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const shown = useMemo(() => filterOptions(options, query), [options, query])
  const toggle = value =>
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  const shownValues = shown.map(o => o.value)

  return (
    <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setQuery('') }}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className={`h-9 justify-between font-normal text-sm ${className}`}>
          <span className="truncate">{triggerText(options, selected, allLabel, noun)}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder={searchPlaceholder} className="h-9 pl-8" />
        </div>
        <div className="mb-1 flex justify-between border-b px-1 pb-2">
          <button type="button" className="text-xs text-primary hover:underline"
            onClick={() => onChange([...new Set([...selected, ...shownValues])])}>
            Select all{query ? ' shown' : ''}
          </button>
          <button type="button" className="text-xs text-muted-foreground hover:underline"
            onClick={() => onChange(query ? selected.filter(v => !shownValues.includes(v)) : [])}>
            Clear{query ? ' shown' : ''}
          </button>
        </div>
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          {shown.length === 0 && <p className="px-1 py-2 text-sm text-muted-foreground">No matches</p>}
          {shown.map(o => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 text-sm hover:bg-accent">
              <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              {o.color && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: o.color }} />}
              <span className="truncate" title={o.label}>{o.label}</span>
              {o.hint != null && <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">{o.hint}</span>}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
